/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { ApiService } from '../../api';
import { Observable } from 'rxjs';
import {
  ArchivedResult,
  CommercialAgreement,
  CommercialAgreementPreview,
  CreateOrUpdateOrderItem,
  DisabledType,
  DisabledTypes,
  FileType,
  LeadsBySearch,
  LeadsPaginationResult,
  OrderNote,
  OrderPrice,
  OrderShipping,
  Quote,
  ShippingDatesUpdate,
  ShippingPaginationResult,
} from '@interfaces';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private url: string = 'Orders';

  constructor(private apiService: ApiService) {}

  public getOrders(
    page: number = 1,
    pageSize: number = 15,
    filter: string,
    businessLines: string[],
    assignedSort: string,
    processingSort: string,
    quotedSort: string,
    scheduledSort: string,
    paidSort: string,
    outForDeliverySort: string,
    afterSaleSort: string,
    agentResponseFilters: string[],
  ): Observable<LeadsPaginationResult> {
    const queryParams: string[] = [
      `Page=${page}`,
      `PageSize=${pageSize}`,
      filter ? `Filter=${encodeURIComponent(filter)}` : '',
      agentResponseFilters?.length
        ? agentResponseFilters
            .map(b => `AgentResponseFilters=${encodeURIComponent(b)}`)
            .join('&')
        : '',
      businessLines?.length
        ? businessLines
            .map(b => `BusinessLines=${encodeURIComponent(b)}`)
            .join('&')
        : '',
      `AssignedSorting=${encodeURIComponent(assignedSort)}`,
      `ProcessingSorting=${encodeURIComponent(processingSort)}`,
      `QuotedSorting=${encodeURIComponent(quotedSort)}`,
      `ScheduledSorting=${encodeURIComponent(scheduledSort)}`,
      `PaidSorting=${encodeURIComponent(paidSort)}`,
      `OutForDeliverySorting=${encodeURIComponent(outForDeliverySort)}`,
      `AfterSaleSorting=${encodeURIComponent(afterSaleSort)}`,
    ].filter(Boolean);

    return this.apiService.get(`orders-follow-up?${queryParams.join('&')}`);
  }

  public getOrder(orderId: string): Observable<any> {
    return this.apiService.get(`${this.url}/${orderId}`);
  }

  public patchStatusOrder(
    orderId: string,
    orderStatusId: number,
  ): Observable<any> {
    return this.apiService.patch(
      `${this.url}/${orderId}/statuses/${orderStatusId}`,
      {},
    );
  }

  public createOrderItem(
    orderId: string,
    body: CreateOrUpdateOrderItem,
  ): Observable<any> {
    return this.apiService.post(`${this.url}/${orderId}/items`, body);
  }

  public updateOrderItem(
    orderId: string,
    orderItemId: string,
    body: CreateOrUpdateOrderItem,
  ): Observable<any> {
    return this.apiService.patch(
      `${this.url}/${orderId}/items/${orderItemId}`,
      body,
    );
  }

  public deleteOrderItem(
    orderId: string,
    orderItemId: string,
  ): Observable<any> {
    return this.apiService.delete(
      `${this.url}/${orderId}/items/${orderItemId}`,
    );
  }

  public updateOrderShipping(
    orderId: string,
    body: OrderShipping,
  ): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/shipping`, body);
  }

  public updateShippingDates(
    orderId: string,
    body: ShippingDatesUpdate,
  ): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/shipping-dates`, body);
  }

  public shippingAvailability(
    orderId: string,
    date: string,
    page: number = 1,
    pageSize: number = 6,
  ): Observable<ShippingPaginationResult> {
    return this.apiService.get(
      `${this.url}/${orderId}/shipping/availability?date=${date}&page=${page}&pageSize=${pageSize}`,
    );
  }

  public updateClosingProbability(orderId: string): Observable<any> {
    return this.apiService.patch(
      `${this.url}/${orderId}/high-closing-probability/toggle`,
      {},
    );
  }

  public getQuoteDetail(orderId: string): Observable<Quote> {
    return this.apiService.get(`${this.url}/${orderId}/quote`);
  }

  public sendQuote(orderId: string, fileTypeNames: string[]): Observable<any> {
    return this.apiService.post(`${this.url}/${orderId}/quote/send`, {
      fileTypeNames,
    });
  }

  /**
   * @function togglePinOrder is called in this way, cause it turns on/off the pin status saved for an specific order
   * @param orderId
   * @returns 204 - Empty response
   */
  public togglePinOrder(orderId: string): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/pinned/toggle`, {});
  }

  public togglePFrameOrder(orderId: string): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/recurring/toggle`, {});
  }

  public saveMessage(orderId: string, request: FormData): Observable<any> {
    return this.apiService.post(`${this.url}/${orderId}/messages`, request);
  }

  public getDisabledTypes(): Observable<DisabledTypes[]> {
    return this.apiService.get(`${this.url}/disabled-types`);
  }

  public disabledOrderId(
    orderId: string,
    disabledType: DisabledType,
  ): Observable<any> {
    return this.apiService.patch(
      `${this.url}/${orderId}/disable`,
      disabledType,
    );
  }

  public getLeadsBySearch(word: string | number): Observable<LeadsBySearch[]> {
    return this.apiService.get(`${this.url}/search?keyword=${word}`);
  }

  public getArchivedLeads(
    type: number,
    page: number,
    pageSize: number,
  ): Observable<ArchivedResult> {
    return this.apiService.get(
      `${this.url}/disabled/order-disabled-type-id/${type}?page=${page}&pageSize=${pageSize}`,
    );
  }

  public getArchivedLeadsBySearch(
    type: number,
    page: number,
    pageSize: number,
    keyword: string,
  ): Observable<ArchivedResult> {
    return this.apiService.get(
      `${this.url}/disabled/order-disabled-type-id/${type}?page=${page}&pageSize=${pageSize}&keyword=${keyword}`,
    );
  }

  public unArchiveLead(orderId: string): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/enable`, {});
  }

  public updatePrice(
    orderId: string,
    orderItemId: string,
    price: OrderPrice,
  ): Observable<any> {
    return this.apiService.patch(
      `${this.url}/${orderId}/items/${orderItemId}/price`,
      price,
    );
  }

  public previewCommercialAgreement(orderId: string): Observable<any> {
    return this.apiService.post(
      `${this.url}/${orderId}/commercial-agreement-preview`,
      {},
    );
  }

  public createCommercialAgreement(
    orderId: string,
    request: CommercialAgreement,
  ): Observable<any> {
    return this.apiService.post(
      `${this.url}/${orderId}/commercial-agreement`,
      request,
    );
  }

  public updateNote(orderId: string, body: OrderNote): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/notes`, body);
  }

  public getCommercialAgreementPreview(
    oderId: string,
  ): Observable<CommercialAgreementPreview> {
    return this.apiService.get(
      `${this.url}/${oderId}/commercial-agreement-preview`,
    );
  }

  public updateReadMessages(orderId: string): Observable<any> {
    return this.apiService.patch(`${this.url}/${orderId}/messages/read`, {});
  }

  public getQuoteDownload(): Observable<FileType> {
    return this.apiService.get(`${this.url}/quote/file-types`);
  }

  public getOrderChat(
    page: number,
    pageSize: number,
    filter: string,
    sorting: string,
    keywordSearch: string,
    useAdvancedSearch: boolean,
    businessLines: string[],
    orderStatuses: string[],
    advancedSorting: string,
    fromDate: string,
    toDate: string,
  ): Observable<any> {
    const queryParams: string[] = [
      `Page=${page}`,
      `PageSize=${pageSize}`,
      filter ? `Filter=${encodeURIComponent(filter)}` : '',
      sorting ? `Sorting=${encodeURIComponent(sorting)}` : '',
      keywordSearch ? `KeywordSearch=${encodeURIComponent(keywordSearch)}` : '',
      useAdvancedSearch
        ? `UseAdvancedSearch=${encodeURIComponent(useAdvancedSearch)}`
        : '',
      businessLines?.length
        ? businessLines
            .map(b => `BusinessLines=${encodeURIComponent(b)}`)
            .join('&')
        : '',
      orderStatuses
        ? orderStatuses
            .map(b => `OrderStatuses=${encodeURIComponent(b)}`)
            .join('&')
        : '',
      advancedSorting
        ? `AdvancedSorting=${encodeURIComponent(advancedSorting)}`
        : '',
      fromDate ? `FromDate=${encodeURIComponent(fromDate)}` : '',
      toDate ? `ToDate=${encodeURIComponent(toDate)}` : '',
    ].filter(Boolean);

    const url = `orders-follow-up-chats?${queryParams.join('&')}`;

    return this.apiService.get(url);
  }

  public getBusinessLinesFilters(): Observable<any> {
    return this.apiService.get('orders-follow-up/filters/business-lines');
  }

  public getFreightZones(businessLineId: number): Observable<any[]> {
    return this.apiService.get(
      `freight-zones?businessLineId=${businessLineId}`,
    );
  }

  public getUnloadingMethods(): Observable<any[]> {
    return this.apiService.get('unloading-methods');
  }
}
